import React, { useMemo, useState } from 'react';
import { ViewLabel } from 'models/view-labels';
import {
  Button,
  Card,
  Col,
  message,
  Row,
  Space,
  Tooltip,
  Typography,
  Descriptions,
  Select,
  List,
  Tag,
} from 'antd';
import { Decision } from '../../../models/reviews';
import { DicomGroup } from 'models/view-labels';
import { BsHandThumbsUp, BsHandThumbsDown } from 'react-icons/bs';
import { VscHistory } from 'react-icons/vsc';
import { getColor } from '../../../theme/features';
import { View } from 'models/views';
import { CoverWrapper, FrameImage } from '../../SegmentationLabels/styled';
import { useCreateViewLabelMutation } from '../../../redux/slices/views';
import { useCreateReviewMutation } from '../../../redux/slices/reviews';
import AcceptModal from './AcceptModal';

// Derive a simple media type from the image URL extension when server doesn't provide one
const getMediaTypeFromUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  const videoExts = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'mpeg', 'mpg', 'ogg'];
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tif', 'tiff', 'svg', 'dcm'];
  try {
    // Handle absolute and relative URLs
    const pathname = new URL(
      url,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    ).pathname;
    const ext = pathname.split('.').pop()?.toLowerCase();
    if (!ext) return undefined;
    if (videoExts.includes(ext)) return 'Video';
    if (imageExts.includes(ext)) return 'Image';
    return undefined;
  } catch (err) {
    // Fallback for malformed/unsupported URLs
    const cleaned = url.split('?')[0].split('#')[0];
    const ext = cleaned.split('.').pop()?.toLowerCase();
    if (!ext) return undefined;
    if (videoExts.includes(ext)) return 'Video';
    if (imageExts.includes(ext)) return 'Image';
    return undefined;
  }
};

interface ViewLabelItemProps {
  dicomGroup: DicomGroup; // New prop for the entire DicomGroup
  onViewLabelToggle: (viewLabelId: string, visible: boolean) => void;
  visibleViewLabels: Set<string>;
  onDicomAccept: (dicomId: string, viewLabels: ViewLabel[]) => void;
  onDicomReject: (dicomId: string, viewLabels: ViewLabel[]) => void;
  dicomReviewed: boolean;
  onLocalDecision: (uuid: string, decision: Decision) => void;
  getLocalDecision: (uuid: string) => Decision | undefined;
  isRejected: boolean;
  allViews: View[];
  localDecisions: Record<string, Decision | undefined>;
  onCreated?: (created: any) => void;
}

const ViewLabelItem: React.FC<ViewLabelItemProps> = props => {
  const {
    dicomGroup,
    onDicomReject,
    dicomReviewed,
    localDecisions, // Destructure localDecisions from props
    allViews,
    onLocalDecision,
  } = props;

  const serverMediaType = dicomGroup.viewLabels[0]?.viewClass?.mediaType;
  const derivedMediaType = getMediaTypeFromUrl(dicomGroup.imageUrl);
  const mediaType = serverMediaType || derivedMediaType;

  // Create a reactive list of view labels that includes local decisions
  const reactiveViewLabels = useMemo(() => {
    return (dicomGroup.viewLabels || []).map(vl => {
      const local = localDecisions[vl.uuid];
      // Prioritize local decision over server's isApproved/isRejected
      const isCurrentlyApproved =
        local === Decision.ACCEPTED ? true : local === Decision.REJECTED ? false : vl.isApproved;
      const isCurrentlyRejected =
        local === Decision.REJECTED ? true : local === Decision.ACCEPTED ? false : vl.isRejected;

      return {
        ...vl,
        isApproved: isCurrentlyApproved,
        isRejected: isCurrentlyRejected,
      };
    });
  }, [dicomGroup.viewLabels, localDecisions]);

  const allViewLabels = dicomGroup.viewLabels || []; // Extract viewLabels from dicomGroup

  const firstViewLabel = reactiveViewLabels.length > 0 ? reactiveViewLabels[0] : undefined;

  const filteredViews = allViews.filter((view: View) => view.type === firstViewLabel?.dicomType);

  const options = filteredViews.map((view: View) => {
    const matchingViewLabel = reactiveViewLabels.find(
      (vl: ViewLabel) => vl.viewClass?.uuid === view.uuid
    );

    const searchableLabel = `${view.view}`;

    // Debugging: Log the searchableLabel to console
    console.log(`View Class UUID: ${view.uuid}, Searchable Label: ${searchableLabel}`);

    return {
      key: view.uuid,
      value: view.uuid,
      label: (
        <Row align="middle" justify="space-between">
          <Col flex="auto">
            <Typography.Text strong>
              {searchableLabel}
              {matchingViewLabel?.user &&
                ` - ${matchingViewLabel.user.firstName} ${matchingViewLabel.user.lastName}`}
            </Typography.Text>
          </Col>
          <Col />
        </Row>
      ),
      searchableLabel: searchableLabel, // Add this line
    };
  });

  const datasourceAndDicomTypeItems = firstViewLabel
    ? [
        {
          key: `datasource-${firstViewLabel.uuid}`,
          label: 'Datasource',
          children: (
            <Tag bordered={false} color={getColor(firstViewLabel.datasourceName)}>
              {firstViewLabel.datasourceName}
            </Tag>
          ),
        },
        {
          key: `dicom_type-${firstViewLabel.uuid}`,
          label: 'DICOM Type',
          children: firstViewLabel.dicomType || 'N/A',
        },
      ]
    : [];

  const [createViewLabel] = useCreateViewLabelMutation();
  const [createReview] = useCreateReviewMutation();
  const [selectedViewUuid, setSelectedViewUuid] = useState<string | null>(null);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);

  const handleAcceptModalOk = async (acceptedId: string | null) => {
    if (!acceptedId) {
      message.warning('Please select a view label to accept.');
      return;
    }

    const reviewsToCreate = (allViewLabels ?? []).map((vl: ViewLabel) => ({
      object_uuid: vl.uuid,
      object_icid: vl.icid,
      object_type: 'VIEW_LABEL',
      object_view: 'default',
      object_view_id: 0,
      decision: vl.uuid === acceptedId ? Decision.ACCEPTED : Decision.REJECTED,
      comment: '',
      annotation_link: '',
    }));

    try {
      await createReview(reviewsToCreate).unwrap();
      message.success('Selected view label accepted and others rejected.');

      (allViewLabels ?? []).forEach(vl => {
        const decision = vl.uuid === acceptedId ? Decision.ACCEPTED : Decision.REJECTED;
        onLocalDecision(vl.uuid, decision);
      });
    } catch (err) {
      message.error('Failed to submit review');
    } finally {
      setIsAcceptModalOpen(false);
    }
  };

  const handleAccept = async () => {
    // Open the accept modal instead of accepting all immediately
    setIsAcceptModalOpen(true);
  };

  const handleReject = async () => {
    // Removed onLocalDecision calls, now relying on onDicomReject to trigger API and refetch
    // dicomGroup.viewLabels.forEach(vl => {
    //   onLocalDecision(vl.uuid, Decision.REJECTED);
    // });
    message.warning('DICOM and all view labels rejected.');
    onDicomReject(dicomGroup.uuid, allViewLabels);
  };

  return (
    <Card
      size="small"
      title={
        <>
          <Typography.Text strong>
            DICOM {dicomGroup.index} (ICID: {dicomGroup.icid})
          </Typography.Text>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {allViewLabels.length} viewLabel{allViewLabels.length !== 1 ? 's' : ''}
          </div>
        </>
      } // Updated Card title
      style={{ marginBottom: '16px' }}
    >
      {dicomGroup.imageUrl && mediaType !== 'Video' && (
        <CoverWrapper>
          <FrameImage src={dicomGroup.imageUrl} alt={`DICOM ${dicomGroup.icid}`} />
        </CoverWrapper>
      )}
      {dicomGroup.imageUrl && mediaType === 'Video' && (
        <CoverWrapper>
          <video
            src={dicomGroup.imageUrl}
            controls
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </CoverWrapper>
      )}
      <div
        style={{
          padding: '10px 0',
          backgroundColor: '#f0f0f0',
          borderRadius: '4px',
          display: 'flex',
          gap: 8,
        }}
      >
        {/* Select for searching view classes; when a view is selected we show a Create button */}
        <Select
          showSearch
          placeholder="Search View Classes"
          optionFilterProp="searchableLabel"
          style={{ flex: 1 }}
          onSelect={_value => {
            // _value is view.uuid per options mapping
            setSelectedViewUuid(String(_value));
          }}
          options={options}
        />
        <Button
          type="primary"
          disabled={!selectedViewUuid}
          onClick={async () => {
            if (!selectedViewUuid) {
              message.warning('Please select a view class before creating.');
              return;
            }

            try {
              // Use the current local view labels for this DICOM instead of calling the listing API
              const existing = (allViewLabels || []).find(
                (vl: any) => vl.viewClass?.uuid === selectedViewUuid
              );

              if (existing) {
                const reviewsToCreate = (allViewLabels || []).map((vl: any) => ({
                  object_uuid: vl.uuid,
                  object_icid: vl.icid,
                  object_type: 'VIEW_LABEL',
                  object_view: 'default',
                  object_view_id: 0,
                  decision: vl.uuid === existing.uuid ? Decision.ACCEPTED : Decision.REJECTED,
                  comment: '',
                  annotation_link: '',
                }));

                if (reviewsToCreate.length > 0) {
                  try {
                    await createReview(reviewsToCreate).unwrap();
                  } catch (reviewErr) {
                    // ignore review errors but proceed to update UI
                  }
                }

                (allViewLabels || []).forEach((vl: any) => {
                  const decision =
                    vl.uuid === existing.uuid ? Decision.ACCEPTED : Decision.REJECTED;
                  onLocalDecision(vl.uuid, decision);
                });

                message.success('Existing view label accepted and other labels rejected.');
                setSelectedViewUuid(null);
                return;
              }

              // Create a new view label and accept it while rejecting the other existing local ones
              const created = await createViewLabel({
                dicom: dicomGroup.uuid,
                view_class: selectedViewUuid,
              }).unwrap();
              message.success('View label created');
              setSelectedViewUuid(null);

              if (typeof props.onCreated === 'function') {
                try {
                  props.onCreated(created);
                } catch (evErr) {
                  // ignore
                }
              }

              const combined = (allViewLabels || []).concat(created);
              const reviewsToCreate = combined.map((vl: any) => ({
                object_uuid: vl.uuid,
                object_icid: vl.icid,
                object_type: 'VIEW_LABEL',
                object_view: 'default',
                object_view_id: 0,
                decision: vl.uuid === created.uuid ? Decision.ACCEPTED : Decision.REJECTED,
                comment: '',
                annotation_link: '',
              }));

              if (reviewsToCreate.length > 0) {
                try {
                  await createReview(reviewsToCreate).unwrap();
                } catch (reviewErr) {
                  // ignore review errors
                }
              }

              combined.forEach((vl: any) => {
                const decision = vl.uuid === created.uuid ? Decision.ACCEPTED : Decision.REJECTED;
                onLocalDecision(vl.uuid, decision);
              });
            } catch (err) {
              message.error('Failed to create view label');
            }
          }}
        >
          Create
        </Button>
      </div>

      {allViewLabels.length > 0 && (
        <div style={{ padding: '10px' }}>
          <div
            style={{
              display: 'flex',
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <Typography.Text strong style={{ minWidth: '80px', marginRight: '0px' }}>
              Views
            </Typography.Text>
            <List
              itemLayout="horizontal"
              size="small"
              dataSource={reactiveViewLabels}
              renderItem={(vl: ViewLabel) => {
                const currentLocalDecision = localDecisions[vl.uuid];
                const isVlAccepted = currentLocalDecision === Decision.ACCEPTED || vl.isApproved;
                const isVlRejected = currentLocalDecision === Decision.REJECTED || vl.isRejected;
                const displayString = `${vl.viewClass?.view || 'N/A'}${vl.user ? ` - ${vl.user.firstName} ${vl.user.lastName}` : ''}`;
                return (
                  <List.Item
                    key={vl.uuid}
                    style={{ padding: '0px', margin: '0px', marginBottom: '16px' }}
                  >
                    <Row align="middle" justify="space-between" style={{ width: '100%' }}>
                      <Col flex="auto" style={{ padding: '0px', margin: '0px' }}>
                        <Typography.Text style={{ padding: '0px', margin: '0px' }}>
                          {displayString}
                        </Typography.Text>
                        <Space size="small" style={{ marginLeft: '8px' }}>
                          {isVlAccepted && (
                            <Tooltip title="Accepted">
                              <BsHandThumbsUp color="green" style={{ fontSize: '14px' }} />
                            </Tooltip>
                          )}
                          {isVlRejected && (
                            <Tooltip title="Rejected">
                              <BsHandThumbsDown color="red" style={{ fontSize: '14px' }} />
                            </Tooltip>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </List.Item>
                );
              }}
            />
          </div>
          <Descriptions
            column={1}
            size="small"
            bordered
            items={datasourceAndDicomTypeItems}
          ></Descriptions>
        </div>
      )}

      {allViewLabels.length === 0 && (
        <div style={{ padding: '10px' }}>
          <Typography.Text type="secondary">No view labels available.</Typography.Text>
        </div>
      )}

      <Row
        justify="space-around"
        style={{ marginTop: '16px', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}
      >
        <Col>
          <Button
            icon={<BsHandThumbsUp />}
            type="text"
            onClick={handleAccept}
            disabled={dicomReviewed}
          />
        </Col>
        <Col>
          <Button icon={<BsHandThumbsDown />} type="text" onClick={handleReject} />
        </Col>
        <Col>
          <Button icon={<VscHistory />} type="text" />
        </Col>
      </Row>

      <AcceptModal
        isModalOpen={isAcceptModalOpen}
        handleCancel={() => setIsAcceptModalOpen(false)}
        handleOk={handleAcceptModalOk}
        viewLabels={allViewLabels}
        initialAcceptedId={reactiveViewLabels.find(v => v.isApproved)?.uuid ?? null}
      />

      {/* <Modal
        title="Review View Label"
        visible={isReviewModalVisible}
        onOk={() => handleReviewModalOk(Decision.ACCEPTED, '')}
        onCancel={handleReviewModalCancel}
      >
        <p>Comment for review (if any):</p>
        <Input type="text" />
      </Modal> */}
    </Card>
  );
};

export default ViewLabelItem;
